import { useState } from "react";
import { Chip } from "@/components/ui";
import { SUBJECT_PICKER } from "@/data/home";

/**
 * "Pick a subject, see the careers" widget. Every subject's careers stay in the DOM (hidden attribute) so the
 * content is crawlable; only the selected subject is shown.
 */
export function SubjectPicker() {
  // Maths (the first subject) is open by default so the widget never looks empty.
  const [selectedId, setSelectedId] = useState(SUBJECT_PICKER.subjects[0].id);
  const selected = SUBJECT_PICKER.subjects.find((s) => s.id === selectedId);

  return (
    <div
      className="mt-12 rounded-4xl border border-brand-200 bg-brand-100 p-6 text-ink sm:p-10 lg:mt-16"
      data-testid="subject-picker"
    >
      <h3 className="max-w-[28ch] font-heading text-2xl font-bold leading-tight sm:text-4xl">
        {SUBJECT_PICKER.title}
      </h3>
      <p className="mt-3 max-w-[56ch] text-base text-ink/70 sm:text-lg">{SUBJECT_PICKER.intro}</p>

      <div className="mt-6 flex flex-wrap gap-2.5 sm:gap-3" role="group" aria-label="Pick a school subject">
        {SUBJECT_PICKER.subjects.map((subject) => (
          <Chip
            key={subject.id}
            tone="onBrand"
            pressed={selectedId === subject.id}
            onClick={() => setSelectedId(subject.id)}
            data-testid={`subject-${subject.id}`}
          >
            {subject.name}
          </Chip>
        ))}
      </div>

      <div className="mt-6 min-h-[7rem]" aria-live="polite">
        {!selected ? (
          <p className="inline-block rounded-full bg-white px-5 py-2.5 font-semibold">{SUBJECT_PICKER.emptyHint}</p>
        ) : null}
        {SUBJECT_PICKER.subjects.map((subject) => (
          <div key={subject.id} hidden={selectedId !== subject.id}>
            <ul className="flex flex-wrap gap-2.5">
              {subject.careers.map((career) => (
                <li
                  key={career.name}
                  className={`animate-pop rounded-full px-4 py-2.5 text-sm font-semibold sm:px-5 sm:text-base ${
                    career.surprise ? "bg-sun-300 text-ink" : "bg-white text-ink"
                  }`}
                >
                  {career.name}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-ink/70 sm:text-base">
              {SUBJECT_PICKER.legend} {subject.name} {SUBJECT_PICKER.factSuffix}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
